/* eslint-disable react/no-array-index-key */
/**
 * Small inline-SVG illustrations used across the wizard. Kept inline
 * (no external image assets) so the bundle stays lean and the colours
 * track the platform's primary palette via CSS-in-JS hex literals.
 */
import { Box } from "@chakra-ui/react";

const GRAD = (id) => (
  <defs>
    <linearGradient id={id} x1="0" x2="1" y1="0" y2="1">
      <stop offset="0" stopColor="#3182CE" />
      <stop offset="1" stopColor="#7C3AED" />
    </linearGradient>
  </defs>
);

// Smart-start hero: speech bubble + sparkles + AI head — signals
// "describe what you want and we'll build it."
export function HeroSmartStart({ size = 180 }) {
  return (
    <Box mx="auto" w={`${size}px`}>
      <svg viewBox="0 0 200 140" width={size} height={(size * 140) / 200}>
        {GRAD("heroGrad")}
        {/* speech bubble */}
        <rect x="20" y="22" width="120" height="60" rx="14" fill="url(#heroGrad)" opacity="0.92" />
        <polygon points="50,82 64,82 50,98" fill="url(#heroGrad)" opacity="0.92" />
        {/* typing dots */}
        <circle cx="56" cy="52" r="4" fill="#fff" />
        <circle cx="78" cy="52" r="4" fill="#fff" />
        <circle cx="100" cy="52" r="4" fill="#fff" />
        {/* sparkles */}
        <g fill="#F59E0B">
          <path d="M155 22 L160 32 L170 36 L160 40 L155 50 L150 40 L140 36 L150 32 Z" />
          <path d="M170 70 L173 76 L179 78 L173 80 L170 86 L167 80 L161 78 L167 76 Z" opacity="0.85" />
          <path d="M138 100 L140 105 L145 107 L140 109 L138 114 L136 109 L131 107 L136 105 Z" opacity="0.7" />
        </g>
      </svg>
    </Box>
  );
}

// Channel picker icons — slightly stylised chat bubble + phone.
export function IconChat({ size = 56, color = "#3182CE" }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size}>
      <rect x="6" y="10" width="52" height="36" rx="10" fill={color} opacity="0.15" />
      <rect x="6" y="10" width="52" height="36" rx="10" stroke={color} strokeWidth="2" fill="none" />
      <circle cx="22" cy="28" r="3" fill={color} />
      <circle cx="32" cy="28" r="3" fill={color} />
      <circle cx="42" cy="28" r="3" fill={color} />
      <path d="M20 46 L20 56 L34 46 Z" fill={color} opacity="0.2" />
      <path d="M20 46 L20 56 L34 46" stroke={color} strokeWidth="2" fill="none" />
    </svg>
  );
}

export function IconVoice({ size = 56, color = "#7C3AED" }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size}>
      <rect x="22" y="10" width="20" height="32" rx="10" fill={color} opacity="0.18" />
      <rect x="22" y="10" width="20" height="32" rx="10" stroke={color} strokeWidth="2" fill="none" />
      <line x1="32" y1="46" x2="32" y2="56" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="24" y1="56" x2="40" y2="56" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M14 28 Q14 42 32 50 Q50 42 50 28" stroke={color} strokeWidth="2" fill="none" />
    </svg>
  );
}

// Use-case industry glyphs — small inline SVGs to brighten Step 2 cards.
export function IndustryGlyph({ industry, size = 40 }) {
  const c = "#7C3AED";
  switch (industry?.toLowerCase()) {
    case "healthcare":
      return (
        <svg viewBox="0 0 48 48" width={size} height={size}>
          <rect x="6" y="14" width="36" height="26" rx="4" fill={c} opacity="0.15" />
          <path d="M22 18 L26 18 L26 24 L32 24 L32 28 L26 28 L26 34 L22 34 L22 28 L16 28 L16 24 L22 24 Z" fill={c} />
        </svg>
      );
    case "restaurant":
      return (
        <svg viewBox="0 0 48 48" width={size} height={size}>
          <path d="M16 8 L16 22 Q16 26 20 26 L20 40 L24 40 L24 8" stroke={c} strokeWidth="2" fill="none" />
          <path d="M32 8 Q28 8 28 18 Q28 24 32 24 L32 40 L36 40 L36 8" stroke={c} strokeWidth="2" fill="none" />
        </svg>
      );
    case "real estate":
    case "real-estate":
      return (
        <svg viewBox="0 0 48 48" width={size} height={size}>
          <polygon points="24,8 40,22 40,40 8,40 8,22" fill={c} opacity="0.15" />
          <polygon points="24,8 40,22 40,40 8,40 8,22" stroke={c} strokeWidth="2" fill="none" />
          <rect x="20" y="28" width="8" height="12" fill={c} opacity="0.6" />
        </svg>
      );
    case "e-commerce":
    case "ecommerce":
      return (
        <svg viewBox="0 0 48 48" width={size} height={size}>
          <path d="M8 12 L12 12 L16 30 L38 30" stroke={c} strokeWidth="2" fill="none" />
          <path d="M16 18 L40 18 L36 30" fill={c} opacity="0.15" />
          <path d="M16 18 L40 18 L36 30" stroke={c} strokeWidth="2" fill="none" />
          <circle cx="20" cy="38" r="3" fill={c} />
          <circle cx="34" cy="38" r="3" fill={c} />
        </svg>
      );
    case "local services":
    case "local-services":
      return (
        <svg viewBox="0 0 48 48" width={size} height={size}>
          <path d="M10 32 L24 18 L38 32 L36 38 L28 38 L28 30 L20 30 L20 38 L12 38 Z" fill={c} opacity="0.15" />
          <path d="M10 32 L24 18 L38 32" stroke={c} strokeWidth="2" fill="none" />
          <path d="M28 38 L28 30 L20 30 L20 38" stroke={c} strokeWidth="2" fill="none" />
        </svg>
      );
    case "saas / b2b":
    case "saas":
      return (
        <svg viewBox="0 0 48 48" width={size} height={size}>
          <rect x="8" y="12" width="32" height="22" rx="3" fill={c} opacity="0.15" />
          <rect x="8" y="12" width="32" height="22" rx="3" stroke={c} strokeWidth="2" fill="none" />
          <line x1="14" y1="20" x2="34" y2="20" stroke={c} strokeWidth="2" />
          <line x1="14" y1="26" x2="28" y2="26" stroke={c} strokeWidth="2" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 48 48" width={size} height={size}>
          <circle cx="24" cy="24" r="16" fill={c} opacity="0.15" />
          <circle cx="24" cy="24" r="16" stroke={c} strokeWidth="2" fill="none" />
        </svg>
      );
  }
}

// Step 7 celebration burst — confetti rays + center check.
export function CelebrationBurst({ size = 120 }) {
  return (
    <Box mx="auto" w={`${size}px`}>
      <svg viewBox="0 0 140 140" width={size} height={size}>
        {GRAD("burstGrad")}
        {/* rays */}
        <g stroke="url(#burstGrad)" strokeWidth="3" strokeLinecap="round">
          <line x1="70" y1="10" x2="70" y2="26" />
          <line x1="70" y1="114" x2="70" y2="130" />
          <line x1="10" y1="70" x2="26" y2="70" />
          <line x1="114" y1="70" x2="130" y2="70" />
          <line x1="28" y1="28" x2="40" y2="40" />
          <line x1="100" y1="100" x2="112" y2="112" />
          <line x1="28" y1="112" x2="40" y2="100" />
          <line x1="100" y1="40" x2="112" y2="28" />
        </g>
        {/* center disc */}
        <circle cx="70" cy="70" r="34" fill="url(#burstGrad)" />
        <path d="M54 71 L66 83 L88 60" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </Box>
  );
}
